
import { defineComponent, ref, PropType, computed, shallowRef, getCurrentInstance, watch } from 'vue'
import { MessageVarBody } from '@t/GCB2'
import { MESSAGE_VARS } from '@/vars/GCB2'
import debounce from 'lodash/debounce'
import differenceBy from 'lodash/differenceBy'
import cloneDeep from 'lodash/cloneDeep'
import { v4 as uuidv4 } from 'uuid'
import BaseInputOutlined from '../../../inputs/BaseInputOutlined.vue'

const COMMON_VAR_IDS = [
    '{название-салона}',
    '{адрес-салона}',
    '{имя-пользователя}',
    '{ссылка-на-отзывы}',
    '{контактный-телефон}',
    '{имя-руководителя}',
    '{имя-администратора}',
    '{ссылка-на-онлайн-запись}',
]

export default defineComponent({
    name: 'MassEditMessageVariablesSettings',
    components: { BaseInputOutlined },
    props: {
        value: { type: Object as PropType<any>, required: true, default: () => ({}) },
        initialClientList: { type: Array as PropType<{ id: string; name: string }[]>, default: () => [] },
        initialVarsExceptions: { type: Array as PropType<MessageVarBody[]>, default: () => [] },
        messageParts: { type: Array as PropType<any[]>, default: () => [] },
    },
    setup(props, { emit }) {
        const root = getCurrentInstance()!.proxy

        // Все переменные, присутствующие в messageParts (без {name})
        const messageVars = computed(() => {
            const parts = props.messageParts || []
            const allTextVariants = parts
                .filter((p: any) => !p?._deleted)
                .map((p: any) => p?.textVariant || '')
                .join(' ')
            return MESSAGE_VARS
                .filter(varItem => varItem.id !== '{name}')
                .filter(varItem => allTextVariants.includes(varItem.id))
        })

        // Разбиение на общие и индивидуальные
        const commonVars = computed(() =>
            messageVars.value.filter(v => COMMON_VAR_IDS.includes(v.id))
        )
        const individualVars = computed(() =>
            messageVars.value.filter(v => !COMMON_VAR_IDS.includes(v.id))
        )

        // Состояние сворачивания
        const commonExpanded = ref(false)
        const individualExpanded = ref(true)

        const startVarsExceptions = shallowRef<MessageVarBody[]>(cloneDeep(props.initialVarsExceptions))
        const varsExceptions = ref<MessageVarBody[]>(cloneDeep(props.initialVarsExceptions))
        const varsExceptionsInit = ref<MessageVarBody[]>(cloneDeep(props.initialVarsExceptions))

        // Возвращает первый exception-элемент для данного varId (ссылка на объект в массиве)
        const getExceptionItem = (varId: string): MessageVarBody | undefined => {
            return varsExceptions.value.find(el => el.varId === varId)
        }

        // Флаг: идёт синхронизация от родителя, не нужно пересчитывать diff
        let isSyncingFromParent = false

        // Обработчик ввода значения переменной
        const onExceptionInput = (varId: string, value: string) => {
            let item = varsExceptions.value.find(el => el.varId === varId)
            if (item) {
                item.value = value
            } else {
                // Переменная не имела сохранённого значения — создаём новую запись
                const newItem: MessageVarBody = {
                    _id: uuidv4(),
                    id: '_DEFAULT_MESSAGE_VAR',
                    value,
                    varId,
                }
                varsExceptions.value.push(newItem)
            }
            emit('exceptions-change', cloneDeep(varsExceptions.value))
            formInput()
        }

        const formInput = debounce(() => {
            if (isSyncingFromParent) return
            emit('validation-updated', messageVars)
            const updatedDiffCond = (o: MessageVarBody) => o.id + o.varId + o.value
            const deletedDiffCond = (o: MessageVarBody) => o.id + o.varId
            const validCond = (o: MessageVarBody) => o.isDefault ? o.id : o.id && o.value.trim()
            const deleted = differenceBy(
                startVarsExceptions.value,
                varsExceptions.value,
                deletedDiffCond
            ).filter(validCond)
            const updated = differenceBy(
                varsExceptions.value,
                startVarsExceptions.value,
                updatedDiffCond
            ).filter(validCond)
            emit('input', { deleted, updated })
        }, 250)

        // Сериализованный ключ для 100% надёжного отслеживания изменений props
        // (deep watcher в Vue 2.7 может пропускать обновления)
        const exceptionsSnapshot = computed(() =>
            JSON.stringify(props.initialVarsExceptions.map(e => ({ id: e.id, v: e.varId, val: e.value })))
        )

        // Синхронизация значений переменных при изменении в другой активации
        watch(exceptionsSnapshot, () => {
            isSyncingFromParent = true
            props.initialVarsExceptions.forEach(newItem => {
                // Поиск по varId + id для корректной работы с переменными,
                // имеющими несколько записей (например {name})
                const existing = varsExceptions.value.find(
                    e => e.varId === newItem.varId && e.id === newItem.id
                )
                if (existing) {
                    if (existing.value !== newItem.value) {
                        existing.value = newItem.value
                    }
                } else {
                    // Переменная была создана в другой активации — добавляем локально
                    varsExceptions.value.push(cloneDeep(newItem))
                }
            })
            // Сбросить флаг после завершения цикла реактивности
            Promise.resolve().then(() => { isSyncingFromParent = false })
        })

        const clear = () => {
            (root.$refs as any).form.resetValidation()
            varsExceptions.value = cloneDeep(varsExceptionsInit.value)
        }

        return {
            formInput,
            clear,
            messageVars,
            commonVars,
            individualVars,
            commonExpanded,
            individualExpanded,
            varsExceptions,
            getExceptionItem,
            onExceptionInput,
        }
    },
})
